import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { trustBadges } from '@/mocks/masterCourseData';
import { useTranslation } from 'react-i18next';

const BADGE_COUNT = 4;
const badgeIndices = Array.from({ length: BADGE_COUNT }, (_, i) => i);

function renderParagraphs(text: string, className: string) {
  return text.split(/\n{2,}/).map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

export default function HeroSection() {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const [badgesVisible, setBadgesVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (textRef.current) {
        textRef.current.classList.add('opacity-100');
        textRef.current.classList.remove('opacity-0', 'translate-y-8');
      }
    }, 200);

    const badgeTimer = setTimeout(() => {
      setBadgesVisible(true);
    }, 600);

    return () => {
      clearTimeout(timer);
      clearTimeout(badgeTimer);
    };
  }, []);

  return (
    <>
    <section className="hero-billboard relative h-[60vh] min-h-[460px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="/images/generated/master-hero-v1-2026.jpg"
          alt={t('coursesMaster.hero.imgAlt')}
          title={t('coursesMaster.hero.imgTitle')}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-primary-900/70 via-primary-900/55 to-primary-900/75"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-accent-500/8 via-transparent to-accent-500/8"></div>
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 w-full">
          <div ref={textRef} className="max-w-2xl opacity-0 translate-y-8 transition-all duration-1000 ease-out">
            
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-50 leading-tight tracking-[0.06em] mb-5">
              {t('coursesMaster.hero.title')}
            </h1>
            {renderParagraphs(
              t('coursesMaster.hero.titleEn'),
              'section-title-en text-accent-400/70 text-lg md:text-xl tracking-[0.15em] mb-6'
            )}
            {renderParagraphs(
              t('coursesMaster.hero.lead'),
              'text-foreground-300/80 text-[17px] leading-relaxed tracking-[0.04em] mb-0 max-w-lg'
            )}
          </div>
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 right-0 bg-primary-900/85 backdrop-blur-sm border-t border-accent-500/15 transition-all duration-700 ${badgesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-4 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
          {badgeIndices.map((index) => (
            <div key={index} className="flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center">
                <i className={`${trustBadges[index]?.icon} text-accent-400 text-lg`}></i>
              </span>
              <span className="text-[14px] text-foreground-200 tracking-[0.05em] whitespace-nowrap">
                {t(`coursesMaster.badge.${index}`)}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-[72px] left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-[12px] text-foreground-400 tracking-[0.15em]">SCROLL</span>
        <div className="w-[1px] h-6 bg-gradient-to-b from-accent-500/30 to-transparent"></div>
      </div>
    </section>
    <nav aria-label="パンくずリスト" className="bg-background-50 border-b border-background-200"><div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-3 flex items-center gap-2 text-[13px] text-foreground-500"><Link to="/" className="hover:text-accent-600 transition-colors">{t('coursesMaster.hero.homeLink')}</Link><i className="ri-arrow-right-s-line text-foreground-400 text-[12px]"></i><Link to="/courses" className="hover:text-accent-600 transition-colors">{t('coursesMaster.hero.coursesLink')}</Link><i className="ri-arrow-right-s-line text-foreground-400 text-[12px]"></i><span className="text-foreground-700">{t('coursesMaster.hero.currentPage')}</span></div></nav>
    </>
  );
}
